import {
  ContentServiceGetStorySearchParamsStringified,
  ContentServiceStory,
} from "@data/content-service-client/types";
import {
  DaperAudio,
  DaperFinanceMoversParams,
  DaperFinanceReferenceParams,
  DaperImages,
  DaperImagesBulkParams,
  DaperImagesSearchParams,
  DaperNewsletter,
  DaperPerson,
  DaperSecurity,
  DaperStory,
  DaperStorySearchParams,
  DaperVideo,
} from "@data/daper-client/types";
import MediaGatewayClient from "@data/media-gateway-client/MediaGatewayClient";
import PersonalizationClient from "@data/personalization-client/PersonalizationClient";
import {
  PersonalizationCFRUParams,
  PersonalizationPopularParams,
  PersonalizationRecommendedPopularParams,
  PersonalizationRecommendedTPRParams,
  PersonalizationRelatedParams,
  PersonalizationResource,
  PersonalizationResourceTypeTags,
} from "@data/personalization-client/types";
import { SrchDataBio, SrchDataGetBioParams } from "@data/srchdata-client/types";
import UserApiClient, {
  TopicParams,
  UserEngagement,
} from "@data/user-api-client/UserApiClient";
import { fetchFrom } from "@util/fetchFrom";

import {
  GetUserProfileParams,
  GetUserBookmarksParams,
  type GetUserProfileData,
  Bookmark,
} from "../data/media-gateway-client/types";
import Singleton from "../data/Singleton";

import { INTERCOM_API_ENDPOINTS, IntercomFunctionKey } from "./enums";
import {
  type IntercomInterface,
  type IntercomParams,
  type IntercomSlots,
} from "./types";

class Intercom extends Singleton implements IntercomInterface {
  slots: IntercomSlots;

  apiEndpoints: { [k in IntercomFunctionKey]?: string };
  personalizationClient: PersonalizationClient;
  mediaGatewayClient: MediaGatewayClient;
  userApiClient: UserApiClient;

  constructor(params?: IntercomParams) {
    super();

    const defaultApiEndpoints = INTERCOM_API_ENDPOINTS;

    this.apiEndpoints = { ...defaultApiEndpoints, ...params?.apiEndpoints };

    this.mediaGatewayClient = params?.mediaGatewayClient;
    this.personalizationClient = params?.personalizationClient;
    this.userApiClient = params?.userApiClient;
  }

  getSlots() {
    return this.slots;
  }

  getSlot(slot: string) {
    return this.getSlots()[slot];
  }

  async getPersonById(params: { id: string }): Promise<DaperPerson> {
    const response = await fetchFrom(this.apiEndpoints.getPersonById, params);

    return response.json();
  }

  async getStories(params: DaperStorySearchParams): Promise<DaperStory[]> {
    const response = await fetchFrom(this.apiEndpoints.getStories, params);

    return response.json();
  }

  async getStoryBySlug(params: { slug: string }): Promise<DaperStory> {
    const response = await fetchFrom(this.apiEndpoints.getStoryBySlug, params);

    return response.json();
  }

  async getStoryById(params: { id: string }): Promise<DaperStory> {
    const response = await fetchFrom(this.apiEndpoints.getStoryById, params);

    return response.json();
  }

  async getImages(params: DaperImagesSearchParams): Promise<DaperImages> {
    const response = await fetchFrom(this.apiEndpoints.getImages, {
      id: params.id,
    });

    return response.json();
  }

  async getAudios(params: { ids: string }): Promise<DaperAudio[]> {
    const response = await fetchFrom(this.apiEndpoints.getAudios, {
      ids: params.ids,
    });

    return response.json();
  }

  async getVideos(params: { id: string }): Promise<DaperVideo[]> {
    const imagesResponse = await fetchFrom(this.apiEndpoints.getVideos, {
      id: params.id,
    });

    return imagesResponse.json();
  }

  async getImagesBulk(
    params: DaperImagesBulkParams,
  ): Promise<Record<string, DaperImages>> {
    const response = await fetchFrom(this.apiEndpoints.getImagesBulk, params);

    return response.json();
  }

  async getSecurities(
    params: DaperFinanceReferenceParams,
  ): Promise<DaperSecurity[]> {
    const response = await fetchFrom(this.apiEndpoints.getSecurities, params);

    return response.json();
  }

  async getPopularResources<T extends PersonalizationResource>(
    params: PersonalizationPopularParams,
  ) {
    const resources =
      await this.personalizationClient.getPopularResources<T>(params);

    return resources || [];
  }

  async getPopularEntities<T extends PersonalizationResourceTypeTags>(
    params: PersonalizationPopularParams,
  ) {
    const resources =
      await this.personalizationClient.getPopularEntities<T>(params);

    return resources || [];
  }

  async getRelated<T extends PersonalizationResource>(
    params: PersonalizationRelatedParams,
  ) {
    const resources = await this.personalizationClient.getRelated<T>(params);

    return resources || [];
  }

  async getRecommended<T extends PersonalizationResource>(
    params: PersonalizationCFRUParams,
  ) {
    const resources = await this.personalizationClient.getCFRU<T>(params);

    return resources || [];
  }

  async getRecommendedPopular<T extends PersonalizationResource>(
    params: PersonalizationRecommendedPopularParams,
  ) {
    const resources =
      await this.personalizationClient.getRecommendedPopular<T>(params);

    return resources || [];
  }

  async getRecommendedTPR<T extends PersonalizationResource>(
    params: PersonalizationRecommendedTPRParams,
  ) {
    const resources =
      await this.personalizationClient.getRecommendedTPR<T>(params);

    return resources || [];
  }

  async getNiCodeStories(
    params: ContentServiceGetStorySearchParamsStringified,
  ): Promise<ContentServiceStory[]> {
    const response = await fetchFrom(
      this.apiEndpoints.getNiCodeStories,
      params,
    );

    return response.json();
  }

  async getNewsletters(): Promise<DaperNewsletter[]> {
    const response = await fetchFrom(this.apiEndpoints.getNewsletters);

    return response.json();
  }

  async getTopMovingSecurities(
    params: DaperFinanceMoversParams,
  ): Promise<DaperSecurity[]> {
    const response = await fetchFrom(
      this.apiEndpoints.getTopMovingSecurities,
      params,
    );

    return response.json();
  }

  getUserAuthors(): Promise<UserEngagement[]> {
    return this.userApiClient.getUserAuthors();
  }

  getUserTopics(): Promise<UserEngagement[]> {
    return this.userApiClient.getUserTopics();
  }

  followTopic(params: TopicParams): Promise<boolean> {
    return this.userApiClient.followTopic(params);
  }

  deleteTopic(params: TopicParams): Promise<boolean> {
    return this.userApiClient.deleteTopic(params);
  }

  getUserSecurities(): Promise<UserEngagement[]> {
    return this.userApiClient.getUserSecurities();
  }

  getUserBookmarks(params: GetUserBookmarksParams): Promise<Bookmark[]> {
    return this.mediaGatewayClient.getUserBookmarks(params);
  }

  getUserProfile(params: GetUserProfileParams): Promise<GetUserProfileData> {
    return this.mediaGatewayClient.getUserProfile(params);
  }

  async getBio(params: SrchDataGetBioParams): Promise<SrchDataBio[]> {
    const response = await fetchFrom(this.apiEndpoints.getBio, params);

    return response.json();
  }
}

export default Intercom;
